import React, { useRef, useEffect } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  TextField,
  Typography,
  Chip,
  IconButton,
  Grid,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  CampaignSipTrunkCreateRequestDto,
  DefaultSipSettingsDto,
} from '@shared/services/apiService/apiService';
import { CampaignTypeEnum, SipTransportEnum } from '@shared/constants/campaign.constants';
import { getCountries } from 'libphonenumber-js';

interface SipSettingsFormProps {
  settings: CampaignSipTrunkCreateRequestDto | null;
  onChange: (settings: CampaignSipTrunkCreateRequestDto | null) => void;
  onValidationChange?: (isValid: boolean) => void;
  onPhoneNumberDraftChange?: (value: string, isValid: boolean, isDuplicate: boolean) => void;
  phoneNumberInputResetKey?: number;
  onAllowedAddressDraftChange?: (value: string, isValid: boolean, isDuplicate: boolean) => void;
  allowedAddressInputResetKey?: number;
  type: 'inbound' | 'outbound';
  disabled?: boolean;
  /** Hide the section title when the parent already provides one (e.g. campaign Sip Trunk tab). */
  hideTitle?: boolean;
}

const CIDR_REGEX =
  /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\/([0-9]|[1-2][0-9]|3[0-2])$/;

const SIP_ADDRESS_IPV4_REGEX =
  /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)(?::\d{1,5})?$/;

const SIP_ADDRESS_HOST_REGEX =
  /^(?=.{1,253}(?::|$))(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}(?::\d{1,5})?$/;

const FROM_HOST_REGEX =
  /^(?=.{1,253}$)[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

const DIAL_PREFIX_REGEX = /^[0-9*#]{1,10}$/;

const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });

const COUNTRY_OPTIONS = getCountries()
  .map((code) => ({ code, label: countryNames.of(code) ?? code }))
  .sort((a, b) => a.label.localeCompare(b.label));

export const SipSettingsForm: React.FC<SipSettingsFormProps> = ({
  settings,
  onChange,
  onValidationChange,
  onPhoneNumberDraftChange,
  phoneNumberInputResetKey,
  onAllowedAddressDraftChange,
  allowedAddressInputResetKey,
  type,
  disabled = false,
  hideTitle = false,
}) => {
  const { t } = useTranslation();
  const phoneNumberInputRef = useRef<HTMLInputElement>(null);
  const allowedAddressInputRef = useRef<HTMLInputElement>(null);
  const [currentInputValue, setCurrentInputValue] = React.useState('');
  const [inputError, setInputError] = React.useState<string | undefined>();
  const [allowedAddressInputValue, setAllowedAddressInputValue] = React.useState('');
  const [allowedAddressInputError, setAllowedAddressInputError] = React.useState<
    string | undefined
  >();

  const hasAdvancedSettings = Boolean(
    settings?.fromHost || settings?.dialCountry || settings?.dialPrefix,
  );
  const [advancedExpanded, setAdvancedExpanded] = React.useState(hasAdvancedSettings);

  // Settings load asynchronously: open the section once saved advanced values arrive
  useEffect(() => {
    if (hasAdvancedSettings) setAdvancedExpanded(true);
  }, [hasAdvancedSettings]);

  const isOutbound = type === CampaignTypeEnum.OUTBOUND;

  const phoneNumberRegex =
    /^(?:\+?[0-9]{2,14}|sips?:[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+(?::\d+)?(?:;[A-Za-z0-9=]+)*|tel:[0-9]+)$/;

  const hasAnySettings =
    settings &&
    (settings.authUsername?.trim() ||
      settings.authPassword?.trim() ||
      settings.address?.trim() ||
      (settings.numbers && settings.numbers.length > 0) ||
      (settings.allowedAddresses && settings.allowedAddresses.length > 0));

  const anySipFieldEntered =
    settings &&
    (settings.authUsername?.trim() ||
      settings.authPassword?.trim() ||
      settings.address?.trim() ||
      (settings.numbers && settings.numbers.length > 0) ||
      (settings.allowedAddresses && settings.allowedAddresses.length > 0));

  const phoneNumbersRequired = anySipFieldEntered;
  const addressRequired = isOutbound && anySipFieldEntered;

  const validatePhoneNumber = (phoneNumber: string): boolean => {
    if (!phoneNumberRegex.test(phoneNumber)) {
      return false;
    }

    const numberPart = phoneNumber.startsWith('+') ? phoneNumber.substring(1) : phoneNumber;

    if (/^(\d)\1+$/.test(numberPart)) {
      return false;
    }

    if (numberPart === '1234567890' || numberPart === '0123456789') {
      return false;
    }

    return true;
  };

  const getFromHostError = (): string | undefined => {
    const fromHost = settings?.fromHost?.trim();
    if (fromHost && !FROM_HOST_REGEX.test(fromHost)) {
      return t('superAdminMainPage.companies.sipSettings.invalidFromHostFormat');
    }
    return undefined;
  };

  const getDialPrefixError = (): string | undefined => {
    const dialPrefix = settings?.dialPrefix?.trim();
    if (!dialPrefix) return undefined;
    if (!DIAL_PREFIX_REGEX.test(dialPrefix)) {
      return t('superAdminMainPage.companies.sipSettings.invalidDialPrefixFormat');
    }
    if (!settings?.dialCountry) {
      return t('superAdminMainPage.companies.sipSettings.dialPrefixRequiresCountry');
    }
    return undefined;
  };

  const validateCidr = (cidr: string): boolean => {
    return CIDR_REGEX.test(cidr);
  };

  const validateSipAddress = (address: string): boolean => {
    const trimmed = address.trim();
    const port = trimmed.split(':')[1];

    if (port !== undefined && (Number(port) < 1 || Number(port) > 65535)) {
      return false;
    }

    return SIP_ADDRESS_IPV4_REGEX.test(trimmed) || SIP_ADDRESS_HOST_REGEX.test(trimmed);
  };

  const getFieldError = (field: keyof DefaultSipSettingsDto): string | undefined => {
    if (!hasAnySettings) return undefined;

    switch (field) {
      case 'address': {
        const address = settings?.address?.trim();
        if (addressRequired && !address) {
          return t('superAdminMainPage.companies.sipSettings.addressRequired');
        }
        if (address && !validateSipAddress(address)) {
          return t('superAdminMainPage.companies.sipSettings.invalidAddressFormat');
        }
        break;
      }
      case 'numbers':
        if (phoneNumbersRequired && (!settings?.numbers || settings.numbers.length === 0)) {
          return t('superAdminMainPage.companies.sipSettings.numbersRequired');
        }
        if (settings?.numbers && settings.numbers.length > 0) {
          const invalidNumber = settings.numbers.find((num) => !validatePhoneNumber(num));
          if (invalidNumber) {
            return t('superAdminMainPage.companies.sipSettings.invalidPhoneNumber');
          }
        }
        break;
    }
    return undefined;
  };

  const getPhoneNumberError = (phoneNumber: string): string | undefined => {
    if (settings?.numbers.includes(phoneNumber)) {
      return t('superAdminMainPage.companies.sipSettings.invalidPhoneNumber');
    }

    if (!phoneNumberRegex.test(phoneNumber)) {
      return t('superAdminMainPage.companies.sipSettings.invalidPhoneNumberFormat');
    }

    const numberPart = phoneNumber.startsWith('+') ? phoneNumber.substring(1) : phoneNumber;
    if (/^(\d)\1+$/.test(numberPart)) {
      return t('superAdminMainPage.companies.sipSettings.phoneNumberCannotBeRepeated');
    }

    if (numberPart === '1234567890' || numberPart === '0123456789') {
      return t('superAdminMainPage.companies.sipSettings.phoneNumberCannotBeSequential');
    }

    return undefined;
  };

  const getCidrError = (cidr: string): string | undefined => {
    if (!validateCidr(cidr)) {
      return t('superAdminMainPage.companies.sipSettings.invalidCidrFormat');
    }
    return undefined;
  };

  const hasValidationErrors = (): boolean => {
    if (!anySipFieldEntered) {
      return false;
    }

    const address = settings?.address?.trim();

    if (isOutbound && addressRequired && !address) {
      return true;
    }

    if (address && !validateSipAddress(address)) {
      return true;
    }

    if (phoneNumbersRequired && (!settings?.numbers || settings.numbers.length === 0)) {
      return true;
    }

    if (settings?.numbers && settings.numbers.length > 0) {
      const hasInvalidNumber = settings.numbers.some((num) => !validatePhoneNumber(num));
      if (hasInvalidNumber) {
        return true;
      }
    }

    if (isOutbound && (getFromHostError() || getDialPrefixError())) {
      return true;
    }

    if (!isOutbound && settings?.allowedAddresses && settings.allowedAddresses.length > 0) {
      const hasInvalidCidr = settings.allowedAddresses.some((addr) => !validateCidr(addr));
      if (hasInvalidCidr) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    if (onValidationChange) {
      const hasErrors = hasValidationErrors();
      onValidationChange(!hasErrors);
    }
  }, [settings, anySipFieldEntered, onValidationChange]);

  useEffect(() => {
    setCurrentInputValue('');
    setInputError(undefined);
  }, [phoneNumberInputResetKey]);

  useEffect(() => {
    setAllowedAddressInputValue('');
    setAllowedAddressInputError(undefined);
  }, [allowedAddressInputResetKey]);

  const handleFieldChange = (field: keyof CampaignSipTrunkCreateRequestDto, value: unknown) => {
    const newSettings: CampaignSipTrunkCreateRequestDto = {
      type,
      authUsername: isOutbound ? '' : undefined,
      authPassword: isOutbound ? '' : undefined,
      numbers: [],
      address: isOutbound ? '' : undefined,
      allowedAddresses: !isOutbound ? [] : undefined,
      transport: undefined,
      ...settings,
      [field]: value,
    };

    onChange(newSettings);
  };

  const handleNumberAdd = (number: string) => {
    const trimmedNumber = number.trim();
    const isDuplicate = settings?.numbers.includes(trimmedNumber) ?? false;

    if (!trimmedNumber || isDuplicate) {
      return;
    }

    if (!validatePhoneNumber(trimmedNumber)) {
      const errorMessage = getPhoneNumberError(trimmedNumber);
      console.warn('Invalid phone number:', trimmedNumber, errorMessage);
      return;
    }

    handleFieldChange('numbers', [...(settings?.numbers || []), trimmedNumber]);
  };

  const handleNumberRemove = (index: number) => {
    const newNumbers = [...(settings?.numbers || [])];
    newNumbers.splice(index, 1);
    handleFieldChange('numbers', newNumbers);
  };

  const handleNumberKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const target = event.target as HTMLInputElement;
      handleNumberAdd(target.value);
      target.value = '';
      setCurrentInputValue('');
      setInputError(undefined);
      onPhoneNumberDraftChange?.('', true, false);
    }
  };

  const handleAddNumberClick = () => {
    if (phoneNumberInputRef.current?.value) {
      handleNumberAdd(phoneNumberInputRef.current.value);
      phoneNumberInputRef.current.value = '';
      setCurrentInputValue('');
      setInputError(undefined);
      onPhoneNumberDraftChange?.('', true, false);
    }
  };

  const handleAllowedAddressAdd = (address: string) => {
    const trimmedAddress = address.trim();
    const isDuplicate = settings?.allowedAddresses?.includes(trimmedAddress) ?? false;

    if (!trimmedAddress || isDuplicate) {
      return;
    }

    if (!validateCidr(trimmedAddress)) {
      console.warn('Invalid CIDR:', trimmedAddress);
      return;
    }

    handleFieldChange('allowedAddresses', [...(settings?.allowedAddresses || []), trimmedAddress]);
  };

  const handleAllowedAddressRemove = (index: number) => {
    const newAddresses = [...(settings?.allowedAddresses || [])];
    newAddresses.splice(index, 1);
    handleFieldChange('allowedAddresses', newAddresses);
  };

  const handleAllowedAddressKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const target = event.target as HTMLInputElement;
      handleAllowedAddressAdd(target.value);
      target.value = '';
      setAllowedAddressInputValue('');
      setAllowedAddressInputError(undefined);
      onAllowedAddressDraftChange?.('', true, false);
    }
  };

  const handleAddAllowedAddressClick = () => {
    if (allowedAddressInputRef.current?.value) {
      handleAllowedAddressAdd(allowedAddressInputRef.current.value);
      allowedAddressInputRef.current.value = '';
      setAllowedAddressInputValue('');
      setAllowedAddressInputError(undefined);
      onAllowedAddressDraftChange?.('', true, false);
    }
  };

  const handleInputChange = (value: string) => {
    const trimmedValue = value.trim();
    const isDuplicate = settings?.numbers.includes(trimmedValue) ?? false;
    const isValid = !trimmedValue || validatePhoneNumber(trimmedValue);

    setCurrentInputValue(value);
    onPhoneNumberDraftChange?.(value, isValid, isDuplicate);
    if (value.trim()) {
      setInputError(getPhoneNumberError(value.trim()));
    } else {
      setInputError(undefined);
    }
  };

  const handleAllowedAddressInputChange = (value: string) => {
    const trimmedValue = value.trim();
    const isDuplicate = settings?.allowedAddresses?.includes(trimmedValue) ?? false;
    const isValid = !trimmedValue || validateCidr(trimmedValue);

    setAllowedAddressInputValue(value);
    onAllowedAddressDraftChange?.(value, isValid, isDuplicate);
    if (value.trim()) {
      setAllowedAddressInputError(getCidrError(value.trim()));
    } else {
      setAllowedAddressInputError(undefined);
    }
  };

  return (
    <Box sx={{ p: hideTitle ? 0 : 2 }}>
      {!hideTitle ? (
        <Typography variant="h6" gutterBottom>
          {t('superAdminMainPage.companies.sipSettings.title', {
            type: type === 'inbound' ? 'Inbound' : 'Outbound',
          })}
        </Typography>
      ) : null}

      <Grid container spacing={2.5}>
        {isOutbound && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('superAdminMainPage.companies.sipSettings.authUsername')}
                value={settings?.authUsername || ''}
                onChange={(e) => handleFieldChange('authUsername', e.target.value)}
                disabled={disabled}
                autoComplete="new-username"
                name="authUsername"
                placeholder={t('superAdminMainPage.companies.sipSettings.authUsernamePlaceholder')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('superAdminMainPage.companies.sipSettings.authPassword')}
                type="password"
                value={settings?.authPassword || ''}
                onChange={(e) => handleFieldChange('authPassword', e.target.value)}
                disabled={disabled}
                autoComplete="new-password"
                name="authPassword"
                placeholder={t('superAdminMainPage.companies.sipSettings.authPasswordPlaceholder')}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label={t('superAdminMainPage.companies.sipSettings.address')}
                value={settings?.address || ''}
                onChange={(e) => handleFieldChange('address', e.target.value)}
                disabled={disabled}
                placeholder={t('superAdminMainPage.companies.sipSettings.addressPlaceholder')}
                error={!!getFieldError('address')}
                helperText={getFieldError('address')}
                autoComplete="off"
                required={!!addressRequired}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label={t('superAdminMainPage.companies.sipSettings.transport')}
                value={settings?.transport || SipTransportEnum.AUTO}
                disabled={disabled}
                onChange={(e) => handleFieldChange('transport', e.target.value)}>
                {Object.values(SipTransportEnum).map((transport) => (
                  <MenuItem key={transport} value={transport}>
                    {transport}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Accordion
                disableGutters
                elevation={0}
                expanded={advancedExpanded}
                onChange={(_, expanded) => setAdvancedExpanded(expanded)}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  '&:before': { display: 'none' },
                }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="sip-advanced-settings-content"
                  id="sip-advanced-settings-header">
                  <Typography fontWeight={600}>
                    {t('superAdminMainPage.companies.sipSettings.advancedSettings')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails id="sip-advanced-settings-content" sx={{ pt: 1 }}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('superAdminMainPage.companies.sipSettings.fromHost')}
                        value={settings?.fromHost || ''}
                        onChange={(e) => handleFieldChange('fromHost', e.target.value || null)}
                        disabled={disabled}
                        placeholder={t(
                          'superAdminMainPage.companies.sipSettings.fromHostPlaceholder',
                        )}
                        error={!!getFromHostError()}
                        helperText={
                          getFromHostError() ||
                          t('superAdminMainPage.companies.sipSettings.fromHostHelper')
                        }
                        autoComplete="off"
                      />
                    </Grid>

                    <Grid item xs={12} sm={8}>
                      <Autocomplete
                        options={COUNTRY_OPTIONS}
                        value={
                          COUNTRY_OPTIONS.find((c) => c.code === settings?.dialCountry) ?? null
                        }
                        onChange={(_, option) =>
                          handleFieldChange('dialCountry', option?.code ?? null)
                        }
                        getOptionLabel={(option) => `${option.label} (${option.code})`}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                        disabled={disabled}
                        autoHighlight
                        id="sip-pbx-region-search"
                        sx={{
                          '& .MuiAutocomplete-inputRoot': {
                            paddingTop: 0,
                            paddingBottom: 0,
                            paddingLeft: 0,
                          },
                          '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                            padding: '12px 14px',
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="sip-pbx-region-search"
                            label={t('superAdminMainPage.companies.sipSettings.dialCountry')}
                            inputProps={{
                              ...params.inputProps,
                              // "search" in id/name + no "country" in the label keeps Safari's contact autofill away
                              autoComplete: 'new-password',
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label={t('superAdminMainPage.companies.sipSettings.dialPrefix')}
                        value={settings?.dialPrefix || ''}
                        onChange={(e) => handleFieldChange('dialPrefix', e.target.value || null)}
                        disabled={disabled}
                        placeholder={t(
                          'superAdminMainPage.companies.sipSettings.dialPrefixPlaceholder',
                        )}
                        error={!!getDialPrefixError()}
                        helperText={getDialPrefixError()}
                        autoComplete="off"
                      />
                    </Grid>

                    <Grid item xs={12} sx={{ pt: '8px !important' }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('superAdminMainPage.companies.sipSettings.dialSettingsHelper')}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </>
        )}

        {!isOutbound && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('superAdminMainPage.companies.sipSettings.addAllowedAddress')}
              placeholder={t('superAdminMainPage.companies.sipSettings.allowedAddressPlaceholder')}
              onKeyPress={handleAllowedAddressKeyPress}
              disabled={disabled}
              inputRef={allowedAddressInputRef}
              value={allowedAddressInputValue}
              onChange={(e) => handleAllowedAddressInputChange(e.target.value)}
              error={!!allowedAddressInputError}
              helperText={allowedAddressInputError}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={handleAddAllowedAddressClick}
                    disabled={
                      disabled || !!allowedAddressInputError || !allowedAddressInputValue.trim()
                    }>
                    <AddIcon />
                  </IconButton>
                ),
              }}
            />

            {settings?.allowedAddresses && settings.allowedAddresses.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {settings.allowedAddresses.map((address, index) => (
                  <Chip
                    key={index}
                    label={address}
                    onDelete={() => handleAllowedAddressRemove(index)}
                    disabled={disabled}
                    color={validateCidr(address) ? 'primary' : 'error'}
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            label={
              phoneNumbersRequired
                ? `${t('superAdminMainPage.companies.sipSettings.addPhoneNumber')} *`
                : t('superAdminMainPage.companies.sipSettings.addPhoneNumber')
            }
            placeholder={t('superAdminMainPage.companies.sipSettings.phoneNumberPlaceholder')}
            onKeyPress={handleNumberKeyPress}
            disabled={disabled}
            inputRef={phoneNumberInputRef}
            value={currentInputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            error={!!getFieldError('numbers') || !!inputError}
            helperText={getFieldError('numbers') || inputError}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={handleAddNumberClick}
                  disabled={disabled || !!inputError || !currentInputValue.trim()}>
                  <AddIcon />
                </IconButton>
              ),
            }}
          />

          {settings?.numbers && settings.numbers.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {settings.numbers.map((number, index) => (
                <Chip
                  key={index}
                  label={number}
                  onDelete={() => handleNumberRemove(index)}
                  disabled={disabled}
                  color={validatePhoneNumber(number) ? 'primary' : 'error'}
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
